import { FC, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { Routes } from 'app/config/routes/consts';
import { CustomOutputFileTypeEnum } from 'entities/Extensions/enums';
import { getCustomOutputFile } from 'entities/Extensions/api';
import { PageHeader } from 'shared/ui/PageHeader';
import { useCustomOutputFileStore } from 'widgets/Extensions/Details/ui/CustomOutputFile/model/state';
import { OutputFieldsView } from './ui/OutputFieldsView';
import { OutputTypeView } from './ui/OutputTypeView';
import { PageWrapper } from 'pages/_layouts/PageWrapper';

export const CustomOutputFileWidget: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { selectedOutputType, resetOutputStore } = useCustomOutputFileStore();
	const [searchParams, setSearchParams] = useSearchParams();

	const typeSearchParam = useMemo(
		() => searchParams.get('type') as CustomOutputFileTypeEnum,
		[searchParams],
	);

	const { data: response, isLoading: isOutputLoading } = useQuery({
		queryKey: ['get/custom-output-file', typeSearchParam],
		queryFn: () => getCustomOutputFile({ type: typeSearchParam }),
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
		enabled: !!typeSearchParam,
	});

	useEffect(() => {
		if (!typeSearchParam) {
			setSearchParams({ type: CustomOutputFileTypeEnum.custom });
			resetOutputStore();
		}
	}, [typeSearchParam]);

	const renderContent = () => {
		if (selectedOutputType) {
			return <OutputFieldsView outputType={typeSearchParam} />;
		}
		if (typeSearchParam && !selectedOutputType) {
			return (
				<OutputTypeView
					loading={isOutputLoading}
					outputTypeData={response?.data || []}
					outputType={typeSearchParam}
				/>
			);
		}
	};
	const renderTitle = () => {
		if (selectedOutputType?.generativeModelId) {
			return selectedOutputType?.generativeModelId.name;
		}

		if (selectedOutputType?.customModelId) {
			return selectedOutputType?.customModelId.name;
		}
		return t('extensions.ex_list.output_file.title');
	};

	const onBackHandler = () => {
		if (selectedOutputType) {
			resetOutputStore();
		} else {
			navigate(Routes.extensions);
		}
	};

	return (
		<PageWrapper>
			<PageHeader
				title={renderTitle()}
				description={t('extensions.ex_list.output_file.page_description')}
				backBtn
				onClick={onBackHandler}
			/>

			{renderContent()}
		</PageWrapper>
	);
};
